import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Mail, Send, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { contactRequestsApi } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import {
    trackContactClick,
    trackFormSubmit,
    type ContactChannel,
} from "@/lib/analytics";
import { SEO, breadcrumbJsonLd, absoluteUrl } from "@/lib/seo";

const Contact = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        service: t("contact.form.service_web"),
        message: ""
    });

    const contactInfo: {
        icon: JSX.Element;
        title: string;
        content: string;
        desc: string;
        link: string;
        channel: ContactChannel;
        ltr?: boolean;
    }[] = [
            {
                icon: <MapPin size={20} />,
                title: t("contact.info.hq_title"),
                content: t("contact.info.hq_address"),
                desc: t("contact.info.hq_desc"),
                link: "https://maps.google.com/?q=Mazzeh+Highway+Damascus",
                channel: "map",
            },
            {
                icon: <Phone size={20} />,
                title: t("contact.info.whatsapp_tr_title"),
                content: "+90 553 732 3153",
                desc: t("contact.info.whatsapp_tr_desc"),
                link: "https://wa.me/905537323153",
                channel: "whatsapp_tr",
                ltr: true,
            },
            {
                icon: <Phone size={20} />,
                title: t("contact.info.whatsapp_sy_title"),
                content: "+963 992 367 582",
                desc: t("contact.info.whatsapp_sy_desc"),
                link: "https://wa.me/963992367582",
                channel: "whatsapp_sy",
                ltr: true,
            },
            {
                icon: <Mail size={20} />,
                title: t("contact.info.email_title"),
                content: "info@trbd.net",
                desc: t("contact.info.email_desc"),
                link: "mailto:info@trbd.net",
                channel: "email",
                ltr: true,
            },
        ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.message) {
            trackFormSubmit("contact", "validation_error", { service: formData.service });
            toast({
                title: t("contact.toast.validation_title"),
                description: t("contact.toast.validation_desc"),
                variant: "destructive"
            });
            return;
        }

        setIsSubmitting(true);
        try {
            await contactRequestsApi.submit(formData);
            setIsSubmitted(true);
            trackFormSubmit("contact", "success", { service: formData.service });
            toast({
                title: t("contact.toast.success_title"),
                description: t("contact.toast.success_desc"),
            });
        } catch (error) {
            trackFormSubmit("contact", "error", { service: formData.service });
            toast({
                title: t("contact.toast.error_title"),
                description: t("contact.toast.error_desc"),
                variant: "destructive"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <SEO
                title={t("meta.contact_title")}
                description={t("meta.contact_description")}
                path="/contact"
                jsonLd={[
                    breadcrumbJsonLd([
                        { name: t("nav.home"), path: "/" },
                        { name: t("nav.contact"), path: "/contact" },
                    ]),
                    {
                        "@context": "https://schema.org",
                        "@type": "ContactPage",
                        name: t("meta.contact_title"),
                        url: absoluteUrl("/contact"),
                    },
                ]}
            />
            <div className="pt-28 sm:pt-32 pb-16 sm:pb-24">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mb-24">
                        <motion.h1
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 tracking-normal"
                            style={{ lineHeight: '1.6' }}
                        >
                            {t("contact.title_prefix")} <br />
                            <span className="text-muted-foreground">{t("contact.title_highlight")}</span>
                        </motion.h1>
                        <p className="text-xl md:text-2xl text-muted-foreground font-medium leading-relaxed max-w-2xl">
                            {t("contact.subtitle")}
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-12 items-start">
                        {/* Info Side */}
                        <div className="lg:col-span-5 space-y-6">
                            {contactInfo.map((item, i) => (
                                <a
                                    key={i}
                                    href={item.link}
                                    onClick={() => trackContactClick(item.channel, "contact_page")}
                                    target={item.link.startsWith('http') ? "_blank" : undefined}
                                    rel={item.link.startsWith('http') ? "noopener noreferrer" : undefined}
                                    className="bento-card p-8 group block hover:bg-white/[0.04] transition-all cursor-pointer"
                                >
                                    <div className="flex items-start gap-5">
                                        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-white group-hover:bg-primary transition-all">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <p className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-1">{item.title}</p>
                                            <h3 className={cn(
                                                "text-xl font-bold text-white mb-1",
                                                item.ltr && "font-mono"
                                            )} dir={item.ltr ? "ltr" : undefined}>
                                                {item.content}
                                            </h3>
                                            <p className="text-sm text-neutral-500 font-medium">{item.desc}</p>
                                        </div>
                                    </div>
                                </a>
                            ))}

                            <div className="p-8 rounded-[2rem] border border-white/5 bg-white/[0.01] mt-12">
                                <h4 className="text-white font-bold mb-6 flex items-center gap-2">
                                    <CheckCircle2 size={18} className="text-primary" />
                                    {t("contact.guarantees_title")}
                                </h4>
                                <ul className="space-y-4 text-sm text-muted-foreground font-medium">
                                    <li>• {t("contact.guarantee_1")}</li>
                                    <li>• {t("contact.guarantee_2")}</li>
                                    <li>• {t("contact.guarantee_3")}</li>
                                </ul>
                            </div>
                        </div>

                        {/* Form Side */}
                        <div className="lg:col-span-7">
                            <AnimatePresence mode="wait">
                                {!isSubmitted ? (
                                    <motion.div
                                        key="form"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        className="bento-card p-10 md:p-16"
                                    >
                                        <form onSubmit={handleSubmit} className="space-y-8">
                                            <div className="grid sm:grid-cols-2 gap-8">
                                                <div className="space-y-3">
                                                    <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">{t("contact.form.name_label")}</label>
                                                    <input
                                                        required
                                                        type="text"
                                                        value={formData.name}
                                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                        placeholder={t("contact.form.name_placeholder")}
                                                        className="w-full bg-white/5 border-b border-white/10 py-3 outline-none focus:border-primary transition-colors text-white font-bold"
                                                    />
                                                </div>
                                                <div className="space-y-3">
                                                    <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">{t("contact.form.email_label")}</label>
                                                    <input
                                                        required
                                                        type="email"
                                                        value={formData.email}
                                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                        placeholder={t("contact.form.email_placeholder")}
                                                        className="w-full bg-white/5 border-b border-white/10 py-3 outline-none focus:border-primary transition-colors text-white font-bold font-mono"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">{t("contact.form.service_label")}</label>
                                                <select
                                                    value={formData.service}
                                                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                                                    className="w-full bg-transparent border-b border-white/10 py-3 outline-none focus:border-primary transition-colors text-white font-bold appearance-none cursor-pointer"
                                                >
                                                    <option className="bg-background">{t("contact.form.service_web")}</option>
                                                    <option className="bg-background">{t("contact.form.service_mobile")}</option>
                                                    <option className="bg-background">{t("contact.form.service_automation")}</option>
                                                    <option className="bg-background">{t("contact.form.service_consulting")}</option>
                                                </select>
                                            </div>

                                            <div className="space-y-3">
                                                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">{t("contact.form.message_label")}</label>
                                                <textarea
                                                    required
                                                    rows={4}
                                                    value={formData.message}
                                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                                    placeholder={t("contact.form.message_placeholder")}
                                                    className="w-full bg-white/5 border-b border-white/10 py-3 outline-none focus:border-primary transition-colors text-white font-bold resize-none"
                                                ></textarea>
                                            </div>

                                            <Button
                                                disabled={isSubmitting}
                                                className="w-full h-16 bg-white text-background hover:bg-neutral-200 font-black text-xl rounded-2xl shadow-tech transition-all group"
                                            >
                                                {isSubmitting ? (
                                                    <Loader2 className="animate-spin" size={24} />
                                                ) : (
                                                    <>
                                                        {t("contact.form.submit")}
                                                        <Send size={20} className="mr-3 group-hover:translate-x-[-4px] group-hover:translate-y-[-4px] transition-transform" />
                                                    </>
                                                )}
                                            </Button>
                                        </form>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="success"
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="bento-card p-16 text-center space-y-8 flex flex-col items-center justify-center min-h-[500px]"
                                    >
                                        <div className="w-24 h-24 rounded-3xl bg-primary/20 flex items-center justify-center text-primary relative">
                                            <Sparkles className="absolute -top-4 -right-4 text-primary animate-pulse" size={32} />
                                            <CheckCircle2 size={48} />
                                        </div>
                                        <div className="space-y-4">
                                            <h2 className="text-4xl font-black text-white">{t("contact.success_title")}</h2>
                                            <p className="text-xl text-muted-foreground font-medium leading-relaxed max-w-sm mx-auto">
                                                {t("contact.success_body")}
                                            </p>
                                        </div>
                                        <Button
                                            onClick={() => setIsSubmitted(false)}
                                            variant="outline"
                                            className="rounded-xl px-12 h-14 border-white/10 hover:bg-white/5 text-white"
                                        >
                                            {t("contact.success_again")}
                                        </Button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Contact;
